/*
Copyright (c) 2012-2023 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

// new logic of sorting items - currently used for UCS enabled merchant

export const DELTA_SORT_ORDER = 10000
type AnyObject = { [key: string]: any }

interface SortedItem {
  resourceID: string
  sortOrder: number
}

const sortMenuItemNewLogicRecursive = ({
  newList,
  newIndex,
  sortKey,
  itemKey,
  result,
}: {
  newList: AnyObject[]
  newIndex: number
  sortKey: string
  itemKey: string
  result: SortedItem[]
}) => {
  let newSortOrder = 0
  let readjustIndex
  // if moved to top, new sort order is current top sort order + delta
  if (newIndex === 0) {
    newSortOrder = newList[1][sortKey] + DELTA_SORT_ORDER
    // if moved to last, new sort order is current last sort order - delta
  } else if (newIndex === newList.length - 1) {
    newSortOrder = newList[newList.length - 2][sortKey] - DELTA_SORT_ORDER
    // else, new sort order will be division of sort order of 2 items on top and bottom of the moved item
  } else {
    const prevIndex = newIndex - 1
    const nextIndex = newIndex + 1
    const sortOrderPrev = newList[prevIndex][sortKey]
    const sortOrderNext = newList[nextIndex][sortKey]
    newSortOrder = Math.round((sortOrderPrev + sortOrderNext) / 2)
    // edge case when the new sort order same with one of, then do recursion to update the other item with same sort order and repeat the process
    // need to check using sortOrderPrev (larger sort order) since Math.round will round up
    if (newSortOrder === sortOrderPrev) {
      readjustIndex = prevIndex
    }
  }
  newList.splice(newIndex, 1, {
    ...newList[newIndex],
    sortOrder: newSortOrder,
  })
  result.push({
    resourceID: newList[newIndex][itemKey],
    sortOrder: newList[newIndex][sortKey],
  })
  if (readjustIndex !== undefined) {
    sortMenuItemNewLogicRecursive({
      newList,
      newIndex: readjustIndex,
      sortKey,
      itemKey,
      result,
    })
  }
}

export const sortMenuItemNewLogic = ({
  newList,
  newIndex,
  sortKey,
  itemKey,
}: {
  newList: AnyObject[]
  newIndex: number
  sortKey: string
  itemKey: string
}) => {
  const result: SortedItem[] = []
  sortMenuItemNewLogicRecursive({
    newIndex,
    newList,
    sortKey,
    itemKey,
    result,
  })
  return result
}

// sorting with the record of accumulated sorted indexes
export const sortMenuItemNewLogicWithSortedIndexes = ({
  originalList,
  sortedIndexes,
  sortKey,
  itemKey,
}: {
  originalList: AnyObject[]
  sortedIndexes: number[][]
  sortKey: string
  itemKey: string
}) => {
  let result: SortedItem[] = []
  const newList = [...originalList]

  sortedIndexes.forEach(sortedIndex => {
    const oldIndex = sortedIndex[0]
    const newIndex = sortedIndex[1]

    // move the item
    const movedItem = newList[oldIndex]
    newList.splice(oldIndex, 1)
    newList.splice(newIndex, 0, movedItem)

    const sortedItems: SortedItem[] = []
    sortMenuItemNewLogicRecursive({
      newList,
      newIndex,
      sortKey,
      itemKey,
      result: sortedItems,
    })
    const sortedIDs = sortedItems.map(sortedItem => sortedItem.resourceID)
    result = [
      ...result.filter(item => !sortedIDs.includes(item.resourceID)),
      ...sortedItems,
    ] // push sorted items and remove previous sorted items with the same id to update the sort order
  })
  return result
}
